import React, { useState, useEffect, useRef } from 'react';
import CustomScheduleForm from './CustomScheduleForm';

const ScheduleModal = ({ automation, onClose, onSave, anchorRef }) => {
  const [interval, setInterval] = useState(automation.schedule?.interval || 60);
  const [saving, setSaving] = useState(false);
  const [position, setPosition] = useState(null);
  // 'list' shows the presets; 'custom' shows the guided builder.
  // Open straight into the builder when the existing schedule is a custom cron.
  const [view, setView] = useState(() =>
    automation.schedule?.enabled &&
    automation.schedule?.cronExpression &&
    !automation.schedule?.interval
      ? 'custom'
      : 'list'
  );
  const popoverRef = useRef(null);

  const intervals = [
    { value: null, label: 'No scheduling' },
    { value: 10, label: 'Every 10 minutes' },
    { value: 30, label: 'Every 30 minutes' },
    { value: 60, label: 'Every hour' },
    { value: 360, label: 'Every 6 hours' },
    { value: 720, label: 'Every 12 hours' },
    { value: 1440, label: 'Every day' },
    { value: 2880, label: 'Every 2 days' },
    { value: 4320, label: 'Every 3 days' },
    { value: 10080, label: 'Every week' },
    { value: 43200, label: 'Every month' },
    { value: 'custom', label: 'Custom…' }
  ];

  // A schedule is "custom" when it is enabled with a cron expression that
  // doesn't map to one of the presets (interval === 0 from cronToInterval).
  const isCustomSchedule =
    automation.schedule?.enabled &&
    automation.schedule?.cronExpression &&
    !automation.schedule?.interval;

  // Calculate position relative to the anchor element with smart positioning
  useEffect(() => {
    if (anchorRef?.current && popoverRef.current) {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();

      const spaceBelow = window.innerHeight - anchorRect.bottom;
      const spaceAbove = anchorRect.top;
      const popoverHeight = popoverRect.height;

      let top, shouldPositionAbove;

      // Position above if not enough space below
      if (spaceBelow < popoverHeight + 16 && spaceAbove > spaceBelow) {
        top = anchorRect.top - popoverHeight - 8;
        shouldPositionAbove = true;
      } else {
        top = anchorRect.bottom + 8;
        shouldPositionAbove = false;
      }

      const left = anchorRect.left;

      // Ensure it doesn't go off-screen horizontally
      const adjustedLeft = Math.min(
        left,
        window.innerWidth - popoverRect.width - 16
      );

      setPosition({ top, left: adjustedLeft, positionAbove: shouldPositionAbove });
    }
  }, [anchorRef]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleSelect = async (selectedInterval) => {
    if (saving) return;

    // Open the guided builder instead of saving immediately.
    if (selectedInterval === 'custom') {
      setView('custom');
      return;
    }

    setSaving(true);
    setInterval(selectedInterval);

    try {
      if (selectedInterval === null) {
        // Disable scheduling
        await onSave(automation.id, { interval: interval || 60, enabled: false });
      } else {
        // Enable/update scheduling
        await onSave(automation.id, { interval: selectedInterval, enabled: true });
      }

      // Close after a brief delay to show the selection
      setTimeout(() => {
        onClose();
      }, 150);
    } catch (error) {
      console.error('Failed to save schedule:', error);
      setSaving(false);
    }
  };

  const handleSaveCustom = async (cronExpression) => {
    if (saving) return;

    setSaving(true);
    try {
      await onSave(automation.id, { cronExpression, enabled: true });
      setTimeout(() => {
        onClose();
      }, 150);
    } catch (error) {
      console.error('Failed to save custom schedule:', error);
      setSaving(false);
    }
  };

  const currentValue = automation.schedule?.enabled ? automation.schedule.interval : null;

  return (
    <>
      {/* Invisible backdrop to catch clicks outside - doesn't block scrolling */}
      <div className="fixed inset-0 z-[60]" onClick={onClose} style={{ pointerEvents: 'none' }}>
        <div className="absolute inset-0" style={{ pointerEvents: 'auto' }} onClick={onClose} />
      </div>

      {/* Popover - only show when positioned to avoid animation glitch */}
      {position && (
        <div
          ref={popoverRef}
          className="fixed z-[70] animate-in fade-in duration-150"
          style={{ top: `${position.top}px`, left: `${position.left}px` }}
        >
          <div className="bg-white dark:bg-gray-900/95 rounded-2xl border border-gray-200 dark:border-white/[0.08] shadow-2xl w-72 overflow-hidden">
            {/* Arrow pointing to button */}
            <div
              className={`absolute left-6 w-4 h-4 bg-white dark:bg-gray-900/95 border-gray-200 dark:border-white/[0.08] transform rotate-45 ${
                position.positionAbove
                  ? '-bottom-2 border-r border-b'
                  : '-top-2 border-l border-t'
              }`}
            />

            {/* Header */}
            <div className="px-5 py-3 border-b border-gray-100 dark:border-white/[0.06]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5 min-w-0">
                  {view === 'custom' && (
                    <button
                      onClick={() => !saving && setView('list')}
                      className="p-1 -ml-1 rounded-lg hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
                      aria-label="Back"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  )}
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                    {view === 'custom' ? 'Custom Schedule' : 'Schedule'}
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 flex-shrink-0"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            {view === 'custom' ? (
              <CustomScheduleForm
                initialCron={isCustomSchedule ? automation.schedule.cronExpression : null}
                onBack={() => !saving && setView('list')}
                onSave={handleSaveCustom}
                saving={saving}
              />
            ) : (
              <div className="p-2 max-h-[400px] overflow-y-auto">
                <div className="space-y-0.5">
                  {intervals.map(int => {
                    const isCustomOption = int.value === 'custom';
                    const isSelected = isCustomOption
                      ? isCustomSchedule
                      : currentValue === int.value;
                    const isCurrentlySaving = saving && interval === int.value;

                    return (
                      <button
                        key={int.value === null ? 'none' : int.value}
                        onClick={() => handleSelect(int.value)}
                        disabled={saving}
                        className={`w-full px-3 py-2 rounded-xl flex items-center justify-between transition-all duration-150 text-left text-sm
                          ${saving ? 'opacity-60 cursor-wait' : ''}
                          ${isSelected
                            ? 'bg-gray-100 dark:bg-white/[0.06] text-gray-900 dark:text-white font-medium'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.04] hover:text-gray-900 dark:hover:text-white'
                          }`}
                      >
                        <span>{int.label}</span>
                        {isCurrentlySaving ? (
                          <svg className="w-4 h-4 animate-spin text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : isCustomOption ? (
                          <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        ) : isSelected ? (
                          <svg className="w-4 h-4 text-blue-500 dark:text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hidden ref for initial measurement */}
      {!position && (
        <div
          ref={popoverRef}
          className="fixed z-[70] opacity-0 pointer-events-none"
          style={{ top: 0, left: 0 }}
        >
          <div className="bg-white dark:bg-gray-900/95 rounded-2xl w-72 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 dark:border-white/[0.06]">
              <div className="text-sm font-semibold">Measuring...</div>
            </div>
            <div className="p-2">
              <div className="space-y-0.5">
                {intervals.map(int => (
                  <div key={int.value === null ? 'none' : int.value} className="px-3 py-2">
                    <span className="text-sm">{int.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ScheduleModal;